<template>
  <div
    class="pricing-card"
    :class="[
      `pricing-card-${variant}`,
      { 'pricing-card-featured': featured },
      { 'pricing-card-popular': popular },
      customClass
    ]"
  >
    <!-- Popular Badge -->
    <!-- <div v-if="popular" class="popular-badge">
      <span class="popular-text">
        <SvgIcon name="award" size="sm" color="white" class="me-1" />
        Most Popular
      </span>
    </div> -->

    <!-- Card Header -->
    <div class="pricing-header">
      <div class="pricing-icon-container" :class="`icon-bg-${iconColor}`">
        <SvgIcon :name="icon" size="2xl" :color="iconColor" class="pricing-icon" />
      </div>

      <div class="pricing-plan-info">
        <h3 class="pricing-title">
          <div class="category-name-split">
            <div class="category-main">{{ getCategoryMainText(title) }}</div>
            <div class="category-sub">{{ getCategorySubText(title) }}</div>
          </div>
        </h3>
        <p class="pricing-subtitle">{{ subtitle }}</p>
      </div>

      <div v-if="discount" class="discount-badge">
        <span class="discount-text">Save {{ discount }}%</span>
      </div>
    </div>

    <!-- Price Display -->
    <div class="pricing-amount">
      <div class="price-container">
        <span class="currency">$</span>
        <span class="price-value">{{ price }}</span>
        <span class="price-period" v-if="period">/{{ period }}</span>
      </div>

      <div v-if="originalPrice" class="original-price">
        <span class="original-amount">${{ originalPrice }}</span>
      </div>

      <div class="price-description">
        {{ priceDescription }}
      </div>
    </div>

    <!-- Features List -->
    <div class="pricing-features">
      <!-- <h4 class="features-title">What's Included</h4> -->
      <ul class="features-list">
        <li
          v-for="feature in features"
          :key="feature.text || feature"
          class="feature-item"
          :class="{ 'feature-disabled': feature.disabled }"
        >
          <div class="feature-icon">
            <SvgIcon
              :name="feature.disabled ? 'x' : 'check-circle'"
              size="sm"
              :color="feature.disabled ? 'muted' : 'success'"
            />
          </div>
          <span class="feature-text">
            {{ feature.text || feature }}
          </span>
          <span v-if="feature.tooltip" class="feature-tooltip" :title="feature.tooltip">
            <SvgIcon name="help-circle" size="xs" color="muted" />
          </span>
        </li>
      </ul>
    </div>

    <!-- Additional Benefits -->
    <div v-if="benefits && benefits.length" class="pricing-benefits">
      <h5 class="benefits-title">Additional Benefits</h5>
      <div class="benefits-grid">
        <div v-for="benefit in benefits" :key="benefit.name" class="benefit-item">
          <SvgIcon :name="benefit.icon" size="lg" :color="benefit.color || 'primary'" class="benefit-icon" />
          <div class="benefit-content">
            <div class="benefit-name">{{ benefit.name }}</div>
            <div class="benefit-value">{{ benefit.value }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Call to Action -->
    <div class="pricing-cta">
      <button
        class="btn pricing-btn"
        :class="[
          featured ? 'btn-gradient-primary' : `btn-outline-${buttonVariant}`,
          { 'btn-disabled': disabled }
        ]"
        :disabled="disabled"
        @click="handleSelectPlan"
      >
        <SvgIcon
          v-if="buttonIcon"
          :name="buttonIcon"
          size="sm"
          :color="featured ? 'white' : buttonVariant"
          class="me-2"
        />
        {{ buttonText }}
      </button>

      <!-- Secondary Actions -->
      <div v-if="secondaryActions && secondaryActions.length" class="secondary-actions">
        <a
          v-for="action in secondaryActions"
          :key="action.text"
          :href="action.href"
          class="secondary-action"
          @click.prevent="handleSecondaryAction(action)"
        >
          <SvgIcon v-if="action.icon" :name="action.icon" size="sm" class="me-1" />
          {{ action.text }}
        </a>
      </div>
    </div>

    <!-- Fine Print -->
    <div v-if="finePrint" class="fine-print">
      {{ finePrint }}
    </div>

    <!-- Background Decorations -->
    <div class="card-glow" :class="`glow-${variant}`"></div>
    <div v-if="featured" class="featured-decoration"></div>
  </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue'

export default {
  name: 'PricingCard',
  components: {
    SvgIcon
  },
  props: {
    // Plan Information
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'users'
    },

    // Pricing
    price: {
      type: [String, Number],
      required: true
    },
    originalPrice: {
      type: [String, Number],
      default: null
    },
    currency: {
      type: String,
      default: '$'
    },
    period: {
      type: String,
      default: ''
    },
    priceDescription: {
      type: String,
      default: ''
    },
    discount: {
      type: [String, Number],
      default: null
    },

    // Features
    features: {
      type: Array,
      required: true,
      default: () => []
    },
    benefits: {
      type: Array,
      default: () => []
    },

    // Styling
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'success', 'warning', 'secondary'].includes(value)
    },
    iconColor: {
      type: String,
      default: 'primary'
    },
    featured: {
      type: Boolean,
      default: false
    },
    popular: {
      type: Boolean,
      default: false
    },

    // Button
    buttonText: {
      type: String,
      default: 'Select Plan'
    },
    buttonIcon: {
      type: String,
      default: 'arrow-right'
    },
    buttonVariant: {
      type: String,
      default: 'primary'
    },
    disabled: {
      type: Boolean,
      default: false
    },

    // Additional
    secondaryActions: {
      type: Array,
      default: () => []
    },
    finePrint: {
      type: String,
      default: ''
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  emits: ['select-plan', 'secondary-action'],
  setup(props, { emit }) {
    const handleSelectPlan = () => {
      if (!props.disabled) {
        emit('select-plan', {
          title: props.title,
          price: props.price,
          variant: props.variant
        })
      }
    }

    const handleSecondaryAction = (action) => {
      emit('secondary-action', action)
    }

    const getCategoryMainText = (categoryName) => {
      // Handle "East Africa" as a special case to keep it together
      if (categoryName.includes("East Africa")) {
        return "East Africa";
      }
      // For other categories, return the first word
      return categoryName.split(' ')[0];
    }

    const getCategorySubText = (categoryName) => {
      // Handle "East Africa" as a special case
      if (categoryName.includes("East Africa")) {
        return "Delegate";
      }
      // For other categories, return remaining words
      return categoryName.split(' ').slice(1).join(' ');
    }

    return {
      handleSelectPlan,
      handleSecondaryAction,
      getCategoryMainText,
      getCategorySubText
    }
  }
}
</script>

<style scoped>
.pricing-card {
  position: relative;
  background: white;
  border: 2px solid var(--idws-border-gray);
  border-radius: 24px;
  padding: var(--spacing-2xl);
  transition: all 0.4s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pricing-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

/* Featured Card */
.pricing-card-featured {
  border-color: var(--idws-bright-blue);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.03) 0%, rgba(255, 255, 255, 1) 100%);
  transform: scale(1.05);
}

.pricing-card-featured:hover {
  transform: scale(1.05) translateY(-8px);
  box-shadow: 0 20px 40px rgba(37, 99, 235, 0.3);
}

/* Popular Badge */
.popular-badge {
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--idws-gradient-accent);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 0 0 16px 16px;
  z-index: 2;
}

.popular-text {
  color: white;
  font-size: var(--font-size-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
}

/* Card Header */
.pricing-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
  position: relative;
}

.pricing-icon-container {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  margin: 0 auto var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.4s ease;
}

.pricing-card:hover .pricing-icon-container {
  transform: scale(1.1);
}

.icon-bg-primary {
  background: var(--idws-gradient-primary);
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.3);
}

.icon-bg-success {
  background: var(--idws-gradient-secondary);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
}

.icon-bg-warning {
  background: var(--idws-gradient-accent);
  box-shadow: 0 8px 24px rgba(251, 191, 36, 0.3);
}

.pricing-icon {
  color: white;
  z-index: 2;
}

.pricing-plan-info {
  text-align: center;
}

.pricing-title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--idws-text-dark);
  margin-bottom: var(--spacing-sm);
  font-family: var(--font-primary);
}

.category-name-split {
  text-align: center;
  line-height: 1.2;
}

.category-main {
  font-size: 1.1em;
  font-weight: 700;
  color: var(--idws-text-dark);
  margin-bottom: 2px;
}

.category-sub {
  font-size: 1.0em;
  font-weight: 700;
  color: var(--idws-text-dark);
}

.pricing-subtitle {
  font-size: var(--font-size-base);
  color: var(--idws-text-muted);
  margin-bottom: 0;
  line-height: 1.5;
}

.discount-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: var(--idws-golden-yellow);
  color: var(--idws-text-dark);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 12px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  transform: rotate(12deg);
}

/* Price Display */
.pricing-amount {
  text-align: center;
  margin-bottom: var(--spacing-2xl);
  padding: var(--spacing-xl);
  background: var(--idws-blue-bg-light);
  border-radius: 20px;
  border: 1px solid var(--idws-blue-bg-soft);
}

.price-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin-bottom: var(--spacing-sm);
}

.currency {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--idws-text-muted);
  margin-right: var(--spacing-xs);
}

.price-value {
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--idws-royal-blue);
  line-height: 1;
  font-family: var(--font-primary);
}

.price-period {
  font-size: var(--font-size-lg);
  color: var(--idws-text-muted);
  margin-left: var(--spacing-xs);
}

.original-price {
  margin-bottom: var(--spacing-sm);
}

.original-amount {
  font-size: var(--font-size-lg);
  color: var(--idws-text-light);
  text-decoration: line-through;
}

.price-description {
  font-size: var(--font-size-base);
  color: var(--idws-text-muted);
  line-height: 1.4;
}

/* Features */
.pricing-features {
  flex-grow: 1;
  margin-bottom: var(--spacing-xl);
}

.features-title {
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--idws-text-dark);
  margin-bottom: var(--spacing-lg);
  text-align: center;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-item {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-sm) 0;
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateX(4px);
}

.feature-item.feature-disabled {
  opacity: 0.5;
}

.feature-icon {
  margin-right: var(--spacing-md);
  flex-shrink: 0;
}

.feature-text {
  flex-grow: 1;
  font-size: var(--font-size-base);
  color: var(--idws-text-dark);
  line-height: 1.4;
}

.feature-tooltip {
  margin-left: var(--spacing-sm);
  cursor: help;
}

/* Benefits */
.pricing-benefits {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--idws-green-bg-light);
  border-radius: 16px;
  border: 1px solid var(--idws-green-bg-soft);
}

.benefits-title {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--idws-text-dark);
  margin-bottom: var(--spacing-md);
  text-align: center;
}

.benefits-grid {
  display: grid;
  gap: var(--spacing-md);
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.benefit-content {
  flex-grow: 1;
}

.benefit-name {
  font-size: var(--font-size-sm);
  color: var(--idws-text-muted);
  line-height: 1.2;
}

.benefit-value {
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--idws-text-dark);
  line-height: 1.2;
}

/* Call to Action */
.pricing-cta {
  text-align: center;
  margin-top: auto;
}

.pricing-btn {
  width: 100%;
  padding: var(--spacing-lg) var(--spacing-xl);
  font-size: var(--font-size-lg);
  font-weight: 600;
  border-radius: 16px;
  transition: all 0.3s ease;
  margin-bottom: var(--spacing-md);
}

.pricing-btn:hover:not(.btn-disabled) {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-actions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-md);
}

.secondary-action {
  color: var(--idws-text-muted);
  text-decoration: none;
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.secondary-action:hover {
  color: var(--idws-bright-blue);
  text-decoration: none;
  transform: translateY(-1px);
}

/* Fine Print */
.fine-print {
  font-size: var(--font-size-xs);
  color: var(--idws-text-light);
  text-align: center;
  margin-top: var(--spacing-md);
  line-height: 1.4;
}

/* Background Effects */
.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  border-radius: inherit;
}

.pricing-card:hover .card-glow {
  opacity: 1;
}

.glow-primary {
  box-shadow: inset 0 0 100px rgba(37, 99, 235, 0.1);
}

.glow-success {
  box-shadow: inset 0 0 100px rgba(34, 197, 94, 0.1);
}

.glow-warning {
  box-shadow: inset 0 0 100px rgba(251, 191, 36, 0.1);
}

.featured-decoration {
  position: absolute;
  top: -50px;
  right: -50px;
  width: 100px;
  height: 100px;
  background: var(--idws-gradient-primary);
  border-radius: 50%;
  opacity: 0.1;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .pricing-card {
    padding: var(--spacing-xl);
    margin-bottom: var(--spacing-xl);
  }

  .pricing-card-featured {
    transform: none;
  }

  .pricing-card-featured:hover {
    transform: translateY(-8px);
  }

  .pricing-icon-container {
    width: 80px;
    height: 80px;
    margin-bottom: var(--spacing-md);
  }

  .pricing-title {
    font-size: var(--font-size-xl);
  }

  .price-value {
    font-size: 2.5rem;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .pricing-btn {
    padding: var(--spacing-md) var(--spacing-lg);
    font-size: var(--font-size-base);
  }
}

@media (max-width: 576px) {
  .pricing-card {
    padding: var(--spacing-lg);
  }

  .pricing-amount {
    padding: var(--spacing-lg);
  }

  .price-value {
    font-size: 2rem;
  }
}
</style>